import { ErrorMessageKey } from '@flexible-reports/store/slices/report-form/validation/validation-messages';
import type { ServerErrorCode, ServerValidationError } from '../server-error-codes';
import { getServerErrorMessageKey } from '../server-error-messages';
import { ApiErrorResponse } from '@flexible-reports/store/api/error-mapping/api-error-types';

type FieldErrorCode =
  | 'NAME_ALREADY_EXISTS'
  | 'INVALID_METRIC_COMBINATION'
  | 'INVALID_DATE_RANGE'
  | 'TOO_MANY_SELECTIONS'
  | 'TOO_MANY_RECURRING_REPORTS';

const FIELD_ERROR_MAP: Record<FieldErrorCode, { path: string[]; messageKey: string }> = {
  NAME_ALREADY_EXISTS: {
    path: ['reportName'],
    messageKey: 'duplicate'
  },
  INVALID_METRIC_COMBINATION: {
    path: ['reportFields', 'columns'],
    messageKey: 'incompatible'
  },
  INVALID_DATE_RANGE: {
    path: ['reportPeriod'],
    messageKey: 'invalid'
  },
  TOO_MANY_SELECTIONS: {
    path: ['reportFields', 'columns'],
    messageKey: 'overLimit'
  },
  TOO_MANY_RECURRING_REPORTS: {
    path: ['reportSchedule', 'frequency'],
    messageKey: 'recurringOverLimit'
  }
};

export const getFieldErrorMessage = (code: FieldErrorCode): ErrorMessageKey => {
  const { path, messageKey } = FIELD_ERROR_MAP[code];
  return `${path.join('.')}.${messageKey}` as ErrorMessageKey;
};

const createFieldError = (code: FieldErrorCode, status?: number): ServerValidationError => ({
  type: 'field',
  severity: 'error',
  status,
  serverCode: code,
  messageKey: getFieldErrorMessage(code),
  path: FIELD_ERROR_MAP[code].path
});

const createGlobalError = (code: ServerErrorCode, status?: number, error?: ApiErrorResponse): ServerValidationError => {
  return {
    type: 'global',
    severity: 'error',
    status,
    serverCode: code,
    headerKey: getServerErrorMessageKey(code, 'header'),
    messageKey: getServerErrorMessageKey(code, 'message'),
    path: ['system', code.toLowerCase()],
    data: error?.data
  };
};

export const createServerError = (code: ServerErrorCode, status?: number, error?: ApiErrorResponse): ServerValidationError => {
  return code in FIELD_ERROR_MAP ? createFieldError(code as FieldErrorCode, status) : createGlobalError(code, status, error);
};
